import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { partnerApiService } from '../../services/api';
import { Discount } from '../../types/api';
import { useCabinet } from '../../contexts/CabinetContext';
import { formatPeriod, periodState } from '../../utils/period';
import { formatPercent } from '../../utils/money';
import { fill } from '../../utils/template';
import { DiscountForm } from '../DiscountForm';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  compareBySize,
  discountCaption,
  discountScope,
  discountSizeLabel,
  discountSizeText,
  isComboWithoutSaving,
  kindOf,
  valueTypeOf,
} from '../discount/discountLabels';

type WeekdayKey = 'dayMon' | 'dayTue' | 'dayWed' | 'dayThu' | 'dayFri' | 'daySat' | 'daySun';

/** Порядок показа Пн…Вс, значения — формат бэка: 0 (вс) … 6 (сб). */
const WEEKDAYS: Array<{ value: number; key: WeekdayKey }> = [
  { value: 1, key: 'dayMon' },
  { value: 2, key: 'dayTue' },
  { value: 3, key: 'dayWed' },
  { value: 4, key: 'dayThu' },
  { value: 5, key: 'dayFri' },
  { value: 6, key: 'daySat' },
  { value: 0, key: 'daySun' },
];

/* Колонка дней фиксированная: семь плашек по 22 px с зазором 3 — 172 px.
   На `1fr` она схлопывалась и налезала на время. Колонка размера шире
   прежней: у комбо в ней цена набора и «вместо 6 500 ₸» второй строкой. */
const TABLE_COLUMNS = 'minmax(200px, 1.7fr) 120px 176px 108px 150px 208px';
const TABLE_MIN_WIDTH = 980;

type SortOption = 'time' | 'created' | 'size';
type KindFilter = 'all' | 'group' | 'combo';
type StateFilter = 'all' | 'live' | 'off';

/** null — дни не заданы: такая акция не идёт ни в один день. */
function daysOf(discount: Discount): number[] {
  if (Array.isArray(discount.weekdays)) return discount.weekdays;
  return discount.weekday != null ? [discount.weekday] : [];
}

/** Тумблер включён. Акции, созданные до этапа 1, приходят без поля — они включены. */
function isEnabled(discount: Discount): boolean {
  return discount.is_active !== false;
}

/**
 * Акция реально доходит до приложения: включена тумблером, сегодня внутри
 * срока, а комбо ещё дешевле своих позиций — иначе гость его не видит
 * (ADR-026 §3). Внутрисуточное окно здесь не учитывается — в кабинете
 * партнёр спрашивает «работает ли эта акция вообще», а не «идёт ли она в
 * эту минуту».
 */
function isLive(discount: Discount): boolean {
  return isEnabled(discount) && periodState(discount) === 'running' && !isComboWithoutSaving(discount);
}

export const DiscountsPage: React.FC = () => {
  const { query, refreshDashboard } = useCabinet();
  const { t, locale } = useLanguage();
  const strings = t.discounts;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<Discount | undefined>();

  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>('time');
  /** id акций, по которым сейчас летит PATCH тумблера. */
  const [togglingIds, setTogglingIds] = useState<number[]>([]);

  const load = async () => {
    try {
      setIsLoading(true);
      setError('');
      setDiscounts(await partnerApiService.getDiscounts());
    } catch (err: any) {
      console.error('Ошибка загрузки скидок:', err);
      setError(err.response?.data?.message || 'Ошибка загрузки скидок');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // «Создать акцию» из шапки приходит сюда параметром — экран открывает
  // форму сразу, без второго клика.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setEditing(undefined);
      setIsFormOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  /**
   * Тумблер в строке. Оптимистично: переключатель отзывается сразу, а на
   * ошибке возвращается назад — иначе клик по акции ощущается как зависание.
   */
  const toggleActive = async (discount: Discount) => {
    const next = !isEnabled(discount);
    setTogglingIds((prev) => [...prev, discount.id]);
    setDiscounts((prev) =>
      prev.map((item) => (item.id === discount.id ? { ...item, is_active: next } : item)),
    );

    try {
      await partnerApiService.setDiscountActive(discount.id, next);
      refreshDashboard();
    } catch (err: any) {
      console.error('Ошибка переключения акции:', err);
      setDiscounts((prev) =>
        prev.map((item) => (item.id === discount.id ? { ...item, is_active: !next } : item)),
      );
      setError(err.response?.data?.message || 'Не удалось переключить акцию');
    } finally {
      setTogglingIds((prev) => prev.filter((id) => id !== discount.id));
    }
  };

  const remove = async (discount: Discount) => {
    if (!window.confirm('Удалить эту акцию? Клиенты перестанут её видеть сразу.')) return;
    try {
      await partnerApiService.deleteDiscount(discount.id);
      setDiscounts((prev) => prev.filter((item) => item.id !== discount.id));
      refreshDashboard();
    } catch (err: any) {
      console.error('Ошибка удаления скидки:', err);
      window.alert(err.response?.data?.message || 'Ошибка удаления скидки');
    }
  };

  const comboCount = discounts.filter((d) => kindOf(d) === 'combo').length;
  const groupCount = discounts.length - comboCount;
  const liveCount = discounts.filter(isLive).length;
  // Средняя и максимальная — только по скидкам в процентах: −500 ₸ и комбо
  // за 5 000 ₸ в проценты не переводятся, а их `discount` — лишь эквивалент.
  const percentSizes = discounts
    .filter((d) => kindOf(d) === 'group' && valueTypeOf(d) === 'percent')
    .map((d) => d.discount);
  const averageDiscount =
    percentSizes.length > 0
      ? Math.round(percentSizes.reduce((sum, size) => sum + size, 0) / percentSizes.length)
      : null;
  const maxDiscount = percentSizes.reduce((best, size) => Math.max(best, size), 0);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const result = discounts.filter((discount) => {
      if (selectedDays.length > 0 && !selectedDays.some((day) => daysOf(discount).includes(day))) return false;
      if (kindFilter !== 'all' && kindOf(discount) !== kindFilter) return false;
      if (stateFilter === 'live' && !isLive(discount)) return false;
      if (stateFilter === 'off' && isLive(discount)) return false;
      if (needle) {
        // Ищется и по подписи размера: «500 ₸» находит скидку в тенге.
        // Неразрывные пробелы подписи цены сводятся к обычным — их и вводят.
        const haystack = [
          discount.name ?? '',
          discountScope(discount, strings, locale),
          discountSizeText(discount, strings),
        ]
          .join(' ')
          .replace(/\u00A0/g, ' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });

    return result.sort((a, b) => {
      if (sortBy === 'time') return (a.time_start || '').localeCompare(b.time_start || '');
      if (sortBy === 'size') return compareBySize(a, b);
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [discounts, selectedDays, kindFilter, stateFilter, sortBy, query, strings, locale]);

  if (isFormOpen) {
    return (
      <DiscountForm
        key={editing?.id ?? 'new'}
        discount={editing}
        onSave={() => {
          setIsFormOpen(false);
          setEditing(undefined);
          load();
          refreshDashboard();
        }}
        onCancel={() => {
          setIsFormOpen(false);
          setEditing(undefined);
        }}
      />
    );
  }

  const kpis = [
    {
      label: t.cabinet.allDiscounts,
      value: discounts.length,
      note: fill(strings.kpiKinds, { group: groupCount, combo: comboCount }),
    },
    // Главная цифра экрана после появления тумблера и срока: «настроено» и
    // «видно гостю» — теперь разные числа.
    { label: t.cabinet.workNow, value: liveCount, note: t.cabinet.workNowNote },
    {
      label: t.cabinet.notWorking,
      value: discounts.length - liveCount,
      note: t.cabinet.notWorkingNote,
    },
    {
      label: t.cabinet.averageDiscount,
      value: averageDiscount != null ? formatPercent(averageDiscount) : '—',
      note: averageDiscount != null ? fill(strings.kpiAverageNote, { max: maxDiscount }) : strings.kpiAverageNone,
    },
  ];

  return (
    <>
      {!isLoading && discounts.length > 0 && (
        <div className="qp-kpi-grid">
          {kpis.map((kpi) => (
            <article key={kpi.label} className="qp-kpi">
              <span className="qp-label">{kpi.label}</span>
              <p className="qp-kpi-value">{kpi.value}</p>
              <span className="qp-kpi-note">{kpi.note}</span>
            </article>
          ))}
        </div>
      )}

      {error && <div className="qp-notice qp-notice--error">{error}</div>}

      {isLoading ? (
        <>
          <div className="qp-skel" style={{ height: 104 }} />
          <div className="qp-skel" style={{ height: 320 }} />
        </>
      ) : discounts.length === 0 ? (
        <div className="qp-empty">
          <h3>{t.cabinet.emptyDiscounts}</h3>
          <p>
            {t.cabinet.emptyDiscountsDesc}
          </p>
          <button
            type="button"
            className="qp-btn qp-btn--primary"
            onClick={() => {
              setEditing(undefined);
              setIsFormOpen(true);
            }}
          >
            {t.cabinet.createDiscount}
          </button>
        </div>
      ) : (
        <>
          <div className="qp-panel" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
            <span className="qp-label">{t.cabinet.days}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {WEEKDAYS.map(({ value, key }) => (
                <button
                  key={value}
                  type="button"
                  className="qp-chip qp-chip--day"
                  aria-pressed={selectedDays.includes(value)}
                  onClick={() =>
                    setSelectedDays((prev) =>
                      prev.includes(value) ? prev.filter((day) => day !== value) : [...prev, value],
                    )
                  }
                >
                  {strings[key]}
                </button>
              ))}
            </div>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select
                className="qp-select"
                value={kindFilter}
                onChange={(event) => setKindFilter(event.target.value as KindFilter)}
                aria-label={strings.filterKind}
              >
                <option value="all">{strings.filterKindAll}</option>
                <option value="group">{strings.filterKindGroup}</option>
                <option value="combo">{strings.filterKindCombo}</option>
              </select>
              <select
                className="qp-select"
                value={stateFilter}
                onChange={(event) => setStateFilter(event.target.value as StateFilter)}
                aria-label="Состояние акции"
              >
                <option value="all">{t.cabinet.allStates}</option>
                <option value="live">{t.cabinet.workNow}</option>
                <option value="off">{t.cabinet.notWorking}</option>
              </select>
              <select
                className="qp-select"
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value as SortOption)}
                aria-label="Сортировка"
              >
                <option value="time">{t.cabinet.sortByTime}</option>
                <option value="size">{t.cabinet.sortBySize}</option>
                <option value="created">{t.cabinet.sortByNew}</option>
              </select>
              {(selectedDays.length > 0 || kindFilter !== 'all' || stateFilter !== 'all') && (
                <button
                  type="button"
                  className="qp-btn qp-btn--ghost"
                  onClick={() => {
                    setSelectedDays([]);
                    setKindFilter('all');
                    setStateFilter('all');
                  }}
                >
                  {t.cabinet.reset}
                </button>
              )}
            </div>
          </div>

          <div className="qp-table">
            <div className="qp-tr qp-thead" style={{ gridTemplateColumns: TABLE_COLUMNS, minWidth: TABLE_MIN_WIDTH }}>
              <span>{t.cabinet.discountCol}</span>
              <span className="qp-td-right">{t.cabinet.discount}</span>
              <span style={{ paddingLeft: 12 }}>{t.cabinet.days}</span>
              <span className="qp-td-right">{t.cabinet.timeCol}</span>
              <span className="qp-td-right">{t.cabinet.periodCol}</span>
              <span className="qp-td-right">{t.cabinet.actionsCol}</span>
            </div>

            {visible.length === 0 ? (
              <div className="qp-tfoot" style={{ justifyContent: 'center', padding: '32px 16px' }}>
                {t.cabinet.noDiscountsFiltered}
              </div>
            ) : (
              visible.map((discount) => {
                const days = daysOf(discount);
                const enabled = isEnabled(discount);
                const state = periodState(discount);
                const scope = discountScope(discount, strings, locale);
                const isCombo = kindOf(discount) === 'combo';
                const size = discountSizeLabel(discount, strings);
                // Название необязательно: без него заголовком остаётся охват
                // (у комбо — состав), и строка не съезжает на «Без названия».
                const title = discount.name?.trim() || scope;
                const subtitle = discount.name?.trim() ? scope : discountCaption(discount, strings);

                return (
                  <div
                    key={discount.id}
                    className={`qp-tr${isLive(discount) ? '' : ' qp-tr--muted'}`}
                    style={{ gridTemplateColumns: TABLE_COLUMNS, minWidth: TABLE_MIN_WIDTH }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span className="qp-cell-title">{title}</span>
                        {/* Вид — метка, а не состояние: нейтральная, зелёный занят
                            выгодой и статусом «работает». */}
                        {isCombo && (
                          <span className="qp-status" style={{ flex: 'none' }}>
                            {strings.badgeCombo.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="qp-cell-sub">{subtitle}</div>
                    </div>

                    {/* Размер в своей единице: «−20 %», «−500 ₸», у комбо — цена
                        набора и обычная цена второй строкой. */}
                    <span
                      className="qp-td-right"
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}
                    >
                      <span
                        className="qp-mono"
                        style={{ fontSize: 15, fontWeight: 600, color: 'var(--qp2-jade)', whiteSpace: 'nowrap' }}
                      >
                        {size.main}
                      </span>
                      {size.sub && (
                        <span
                          className="qp-mono"
                          style={{
                            fontSize: 11,
                            whiteSpace: 'nowrap',
                            color: size.warn ? 'var(--qp2-amber-ink)' : 'var(--qp2-faint)',
                          }}
                          title={size.warn ? strings.comboNoSavingTitle : undefined}
                        >
                          {' '}
                          {size.sub}
                        </span>
                      )}
                    </span>

                    <div style={{ display: 'flex', gap: 3, paddingLeft: 12 }}>
                      {WEEKDAYS.map(({ value, key }) => (
                        <span
                          key={value}
                          className={`qp-daydot${days.includes(value) ? ' qp-daydot--on' : ''}`}
                          title={strings[key]}
                        >
                          {strings[key]}
                        </span>
                      ))}
                    </div>

                    <span className="qp-mono qp-td-right" style={{ fontSize: 12.5 }}>
                      {discount.time_start && discount.time_end
                        ? `${discount.time_start.slice(0, 5)}–${discount.time_end.slice(0, 5)}`
                        : '—'}
                    </span>

                    <span
                      className="qp-td-right"
                      style={{ fontSize: 11.5, color: state === 'running' ? 'var(--qp2-muted)' : 'var(--qp2-faint)' }}
                    >
                      {state === 'expired' ? t.cabinet.expired : formatPeriod(discount)}
                    </span>

                    <span
                      className="qp-row-actions"
                      style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}
                    >
                      {/* Тумблер левее кнопок: он переключает состояние, а не
                          открывает экран, и стоять в одном ряду с «Удалить»
                          ему незачем. */}
                      <button
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        aria-label={enabled ? 'Выключить акцию' : 'Включить акцию'}
                        title={enabled ? 'Выключить акцию' : 'Включить акцию'}
                        className="qp-switch"
                        disabled={togglingIds.includes(discount.id)}
                        onClick={() => toggleActive(discount)}
                      >
                        <span />
                      </button>
                      <button
                        type="button"
                        className="qp-btn qp-btn--outline qp-btn--sm"
                        onClick={() => {
                          setEditing(discount);
                          setIsFormOpen(true);
                        }}
                      >
                        {t.common.edit}
                      </button>
                      <button
                        type="button"
                        className="qp-btn qp-btn--danger qp-btn--sm"
                        onClick={() => remove(discount)}
                        aria-label="Удалить акцию"
                      >
                        {t.common.delete}
                      </button>
                    </span>
                  </div>
                );
              })
            )}

            <div className="qp-tfoot">
              <span>
                {t.cabinet.shown} {visible.length} {t.cabinet.outOf} {discounts.length}
              </span>
              <button
                type="button"
                className="qp-btn qp-btn--outline qp-btn--sm"
                style={{ marginLeft: 'auto' }}
                onClick={() => navigate('/catalog')}
              >
                {t.cabinet.openCatalog}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
